export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

export type ActiveHours = {
  days: Weekday[];
  start: string; // HH:MM
  end: string; // HH:MM
};

export type Holiday = {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
};

export type Channel = {
  type: "sms";
  to: string;
  enabled: boolean;
};

export type Recipient = {
  name: string;
  timezone: string;
  activeHours: ActiveHours[];
  holidays: Holiday[];
  channels: Channel[];
};

export type MessageTemplateProperties = {
  customHeaderText: string | null;
  showLocation: boolean;
  showDate: boolean;
  showAlarmState: boolean;
  showDescription: boolean;
};

export type NotifyOn = {
  alarmActivation: boolean;
  alarmDeactivation: boolean;
  alarmAcknowledgement: boolean;
  deviceDisconnection: boolean;
};

export const DEFAULT_NOTIFY_ON: NotifyOn = {
  alarmActivation: false,
  alarmDeactivation: false,
  alarmAcknowledgement: false,
  deviceDisconnection: false,
};

// Active hours preset options
export type ActiveHoursPreset =
  | "always"
  | "office-hours" // Mon-Fri 08:00-17:00
  | "extended-hours" // Mon-Fri 07:00-19:00
  | "business-days" // Mon-Fri 00:00-24:00
  | "daytime" // Every day 06:00-22:00
  | "custom";

export const ACTIVE_HOURS_PRESETS: Array<{
  value: ActiveHoursPreset;
  label: string;
  description?: string;
}> = [
  {
    value: "office-hours",
    label: "Mon-Fri 07:00-15:00",
  },
  {
    value: "extended-hours",
    label: "Mon-Fri 08:00-16:00",
  },
  {
    value: "business-days",
    label: "Mon-Fri 15:00-07:00 & Weekends 24/7",
  },
  {
    value: "daytime",
    label: "Mon-Fri 16:00-08:00 & Weekends 24/7",
  },
  { value: "always", label: "Always (24/7)" },
];

export type DeliveryChannels = {
  sms: boolean;
  email: boolean;
};

/** Form state held by the UI. Not sent to the API directly. */
export type AlarmNotificationFormState = {
  timezone: string;
  activeHoursPreset: ActiveHoursPreset;
  customActiveHours?: ActiveHours[]; // Only used when preset is "custom"
  alarmGroupNames: string[];
  deliveryChannels: DeliveryChannels;
  messageTemplateProperties: MessageTemplateProperties;
  notifyOn: NotifyOn;
};

/** Resolved payload sent to the API. */
export type AlarmNotificationConfig = {
  timezone: string; // IANA timezone identifier (e.g., "Europe/Helsinki")
  activeHours: ActiveHours[];
  alarmGroupNames: string[];
  deliveryChannels: DeliveryChannels;
  messageTemplateProperties: MessageTemplateProperties;
  notifyOn: NotifyOn;
};

// Priority timezones shown first in the autocomplete
const PRIORITY_TIMEZONES = [
  "Europe/Helsinki", // Finland
  "Europe/Stockholm", // Sweden
  "Europe/Paris", // France
];

function getTimezoneCityName(timezone: string): string {
  const parts = timezone.split("/");
  const city = parts[parts.length - 1] ?? timezone;
  return city.replaceAll("_", " ");
}

function getTimezoneSortGroup(timezone: string): number {
  if (PRIORITY_TIMEZONES.includes(timezone)) {
    return 0;
  }

  if (timezone.startsWith("Europe/")) {
    return 1;
  }

  return 2;
}

// Get all supported IANA timezones from the browser, sorted with priority countries first
export function getAllTimezones(): string[] {
  let timezones: string[];
  try {
    timezones = Intl.supportedValuesOf("timeZone");
  } catch {
    // Fallback for older browsers - just return priority + some common ones
    timezones = [
      ...PRIORITY_TIMEZONES,
      "Europe/London",
      "Europe/Berlin",
      "America/New_York",
      "America/Los_Angeles",
      "Asia/Tokyo",
      "UTC",
    ];
  }

  // Sort: priority timezones first, then Europe, then the rest by city name.
  return timezones.sort((a, b) => {
    const aPriority = PRIORITY_TIMEZONES.indexOf(a);
    const bPriority = PRIORITY_TIMEZONES.indexOf(b);
    const aIsPriority = aPriority !== -1;
    const bIsPriority = bPriority !== -1;

    if (aIsPriority && bIsPriority) {
      return aPriority - bPriority;
    }
    if (aIsPriority) return -1;
    if (bIsPriority) return 1;

    const groupDifference = getTimezoneSortGroup(a) - getTimezoneSortGroup(b);
    if (groupDifference !== 0) {
      return groupDifference;
    }

    const cityDifference = getTimezoneCityName(a).localeCompare(
      getTimezoneCityName(b),
    );
    if (cityDifference !== 0) {
      return cityDifference;
    }

    return a.localeCompare(b);
  });
}

// Get browser's current timezone
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// Get UTC offset string for a timezone (e.g., "UTC+03:00" or "UTC-05:00")
// Cache offsets since creating Intl.DateTimeFormat is expensive
const offsetCache = new Map<string, string>();

export function getTimezoneOffset(timezone: string): string {
  const cached = offsetCache.get(timezone);
  if (cached) return cached;

  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "shortOffset",
    });
    const parts = formatter.formatToParts(now);
    const offsetPart = parts.find((p) => p.type === "timeZoneName");
    // Browser returns "GMT+X", replace with "UTC+X"
    const offset = offsetPart?.value.replace("GMT", "UTC") || "UTC";
    offsetCache.set(timezone, offset);
    return offset;
  } catch {
    return "UTC";
  }
}

// Get timezone with offset label (e.g., "Europe/Helsinki (UTC+03:00)")
export function formatTimezoneWithOffset(timezone: string): string {
  const offset = getTimezoneOffset(timezone);
  return `${timezone} (${offset})`;
}

// Get all timezones with their UTC offsets for display
export type TimezoneOption = {
  value: string; // IANA timezone id
  label: string; // Display label with offset
};

// Cache the full options list - computed once on first access
let cachedTimezoneOptions: TimezoneOption[] | null = null;

export function getTimezoneOptions(): TimezoneOption[] {
  if (cachedTimezoneOptions) return cachedTimezoneOptions;

  cachedTimezoneOptions = getAllTimezones().map((tz) => ({
    value: tz,
    label: formatTimezoneWithOffset(tz),
  }));
  return cachedTimezoneOptions;
}

// FIXME: Fetch available alarm groups from API
export const AVAILABLE_ALARM_GROUPS = [
  { id: "heating-failure", name: "Heating Failure" },
  { id: "ventilation-alarm", name: "Ventilation Alarm" },
  { id: "fire-alarm", name: "Fire Alarm" },
  { id: "security-alarm", name: "Security Alarm" },
  { id: "water-leak", name: "Water Leak" },
];

export const WEEKDAY_ORDER: Weekday[] = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
];

const WEEKDAYS_MON_FRI: Weekday[] = ["mon", "tue", "wed", "thu", "fri"];
const ALL_DAYS: Weekday[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/** Resolved ActiveHours[] for each named preset. */
const PRESET_ACTIVE_HOURS: Record<
  Exclude<ActiveHoursPreset, "custom">,
  ActiveHours[]
> = {
  always: [{ days: ALL_DAYS, start: "00:00", end: "00:00" }],
  "office-hours": [{ days: WEEKDAYS_MON_FRI, start: "07:00", end: "15:00" }],
  "extended-hours": [{ days: WEEKDAYS_MON_FRI, start: "08:00", end: "16:00" }],
  "business-days": [
    { days: WEEKDAYS_MON_FRI, start: "15:00", end: "07:00" },
    { days: ["sat", "sun"], start: "00:00", end: "00:00" },
  ],
  daytime: [
    { days: WEEKDAYS_MON_FRI, start: "16:00", end: "08:00" },
    { days: ["sat", "sun"], start: "00:00", end: "00:00" },
  ],
};

export const EMPTY_FORM_STATE: AlarmNotificationFormState = {
  timezone: getBrowserTimezone(),
  activeHoursPreset: "always",
  alarmGroupNames: [],
  deliveryChannels: {
    sms: false,
    email: false,
  },
  messageTemplateProperties: {
    customHeaderText: "",
    showLocation: false,
    showDate: false,
    showAlarmState: false,
    showDescription: false,
  },
  notifyOn: { ...DEFAULT_NOTIFY_ON },
};

function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

/**
 * Splits any entry whose time range crosses midnight into two entries — one
 * ending at "00:00" (meaning 24:00 / end-of-day per the assets convention)
 * and one starting at "00:00" on the following day(s).
 *
 * This mirrors the normalization the admin API applies before storing, so that
 * detectPreset can compare against canonical forms regardless of whether the
 * input came from a preset definition or from assets directly.
 */
export function normalizeActiveHoursEntries(
  entries: ActiveHours[],
): ActiveHours[] {
  return entries.flatMap((entry) => {
    if (entry.end === "00:00") return [entry];
    if (timeToMinutes(entry.start) < timeToMinutes(entry.end)) return [entry];
    return [
      { days: entry.days, start: entry.start, end: "00:00" },
      {
        days: entry.days.map(
          (d) =>
            WEEKDAY_ORDER[
              (WEEKDAY_ORDER.indexOf(d) + 1) % WEEKDAY_ORDER.length
            ],
        ),
        start: "00:00",
        end: entry.end,
      },
    ];
  });
}

/**
 * Attempts to match a resolved ActiveHours[] back to one of the named presets.
 * Both the input and each preset definition are normalised (overnight entries
 * split, day order within entries canonicalised, entry order canonicalised)
 * before comparison, so the result is stable regardless of how assets stored
 * or returned the data.
 *
 * If the hours no longer match any known preset — for example because the
 * preset definition was updated on the admin side after the config was saved —
 * it falls back to "custom", preserving the exact hours for editing.
 */
export function detectPreset(activeHours: ActiveHours[]): ActiveHoursPreset {
  const canonicalizeEntry = (entry: ActiveHours) => ({
    days: [...entry.days].sort(
      (a, b) => WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b),
    ),
    start: entry.start,
    end: entry.end,
  });

  const canonicalizeHours = (hours: ActiveHours[]) =>
    normalizeActiveHoursEntries(hours)
      .map(canonicalizeEntry)
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));

  const serializedInput = JSON.stringify(canonicalizeHours(activeHours));

  for (const [preset, hours] of Object.entries(PRESET_ACTIVE_HOURS) as Array<
    [Exclude<ActiveHoursPreset, "custom">, ActiveHours[]]
  >) {
    if (JSON.stringify(canonicalizeHours(hours)) === serializedInput) {
      return preset;
    }
  }

  return "custom";
}

/**
 * Validates that an unknown value from the API is a well-shaped AlarmNotificationConfig.
 * Throws a descriptive error on any shape mismatch — never silently coerces or returns undefined.
 */
export function parseAlarmNotificationConfig(
  data: unknown,
): AlarmNotificationConfig {
  if (data === null || typeof data !== "object") {
    throw new Error(
      `Expected an object, got ${data === null ? "null" : typeof data}`,
    );
  }

  const obj = data as Record<string, unknown>;

  if (typeof obj.timezone !== "string" || !obj.timezone) {
    throw new Error('"timezone" must be a non-empty string');
  }

  if (!Array.isArray(obj.activeHours)) {
    throw new Error('"activeHours" must be an array');
  }

  for (let i = 0; i < obj.activeHours.length; i++) {
    const entry = obj.activeHours[i] as Record<string, unknown> | undefined;
    if (!entry || typeof entry !== "object") {
      throw new Error(`activeHours[${i}] must be an object`);
    }
    if (!Array.isArray(entry.days) || entry.days.length === 0) {
      throw new Error(`activeHours[${i}].days must be a non-empty array`);
    }
    if (typeof entry.start !== "string") {
      throw new Error(`activeHours[${i}].start must be a string`);
    }
    if (typeof entry.end !== "string") {
      throw new Error(`activeHours[${i}].end must be a string`);
    }
  }

  if (!Array.isArray(obj.alarmGroupNames)) {
    throw new Error('"alarmGroupNames" must be an array');
  }

  if (
    typeof obj.messageTemplateProperties !== "object" ||
    obj.messageTemplateProperties === null
  ) {
    throw new Error('"messageTemplateProperties" must be an object');
  }

  if (typeof obj.notifyOn !== "object" || obj.notifyOn === null) {
    throw new Error('"notifyOn" must be an object');
  }

  if (
    typeof obj.deliveryChannels !== "object" ||
    obj.deliveryChannels === null
  ) {
    throw new Error('"deliveryChannels" must be an object');
  }

  return data as AlarmNotificationConfig;
}

export function normalizeAlarmNotificationConfig(
  values: AlarmNotificationFormState,
): AlarmNotificationConfig {
  const activeHours =
    values.activeHoursPreset === "custom"
      ? (values.customActiveHours ?? [])
      : PRESET_ACTIVE_HOURS[values.activeHoursPreset];

  return {
    timezone: values.timezone,
    activeHours,
    alarmGroupNames: values.alarmGroupNames
      .map((g) => g.trim())
      .filter(Boolean),
    deliveryChannels: {
      sms: values.deliveryChannels.sms,
      email: values.deliveryChannels.email,
    },
    messageTemplateProperties: {
      ...values.messageTemplateProperties,
      customHeaderText:
        values.messageTemplateProperties.customHeaderText?.trim() ?? null,
    },
    notifyOn: {
      ...values.notifyOn,
    },
  };
}

export function getActiveHoursLabel(val: string): string {
  const hours = JSON.parse(val);

  if (!Array.isArray(hours)) {
    return val;
  }

  const preset = detectPreset(hours);

  return ACTIVE_HOURS_PRESETS.find((p) => p.value === preset)?.label ?? preset;
}
